import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Badge,
  Button,
  Spinner,
  Modal,
  Pagination
} from 'react-bootstrap';
import { adminService } from '../../services/adminService';

interface ContactQuery {
  id: string;
  name: string;
  mobile: string;
  email: string;
  feedback_type: string;
  feedback: string;
  status: string;
  created_at: string;
}

const ContactReportsPage: React.FC = () => {
  const [queries, setQueries] = useState<ContactQuery[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState<ContactQuery | null>(null);
  const [showModal, setShowModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [pagination, setPagination] = useState({
    page: 1,
    limit: itemsPerPage,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  useEffect(() => {
    fetchQueries();
  }, [currentPage]);

  const fetchQueries = async () => {
    try {
      setLoading(true);

      const res = await adminService.getContactQueries({
        page: currentPage,
        limit: itemsPerPage,
      });

      if (res.success) {
        setQueries(res.data || []);

        const meta = res.pagination;
        const total = Number(meta?.total ?? 0);
        const limit = Number(meta?.limit ?? itemsPerPage) || itemsPerPage;
        const totalPages = Number(meta?.totalPages ?? 0);
        const page = Number(meta?.page ?? currentPage);

        setPagination({
          page,
          limit,
          total,
          totalPages,
          hasNext: Boolean(meta?.hasNext),
          hasPrev: Boolean(meta?.hasPrev),
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber === currentPage) return;
    if (pageNumber < 1) return;
    if (pagination.totalPages && pageNumber > pagination.totalPages) return;
    setCurrentPage(pageNumber);
  };

  const handleView = (query: ContactQuery) => {
    setSelectedQuery(query);
    setShowModal(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge bg="danger">New</Badge>;
      case 'in_progress':
        return <Badge bg="warning">In Progress</Badge>;
      case 'resolved':
        return <Badge bg="success">Resolved</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const startItem =
    pagination.total > 0
      ? (pagination.page - 1) * pagination.limit + 1
      : 0;

  const endItem =
    pagination.total > 0
      ? Math.min(pagination.total, pagination.page * pagination.limit)
      : 0;

  return (
    <>
      <Card>
        <Card.Header>
          <h5>Contact / Feedback Reports ({pagination.total})</h5>
        </Card.Header>

        <Card.Body>
          {loading ? (
            <Spinner animation="border" />
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Type</th>
                  {/* <th>Status</th> */}
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {queries.map((query) => (
                  <tr key={query.id}>
                    <td>{query.name}</td>
                    <td>{query.email}</td>
                    <td>{query.mobile}</td>
                    <td>{query.feedback_type}</td>
                    {/* <td>{getStatusBadge(query.status)}</td> */}
                    <td>
                      {new Date(query.created_at).toLocaleString()}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleView(query)}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>

        {/* Pagination Footer */}
        {pagination.totalPages > 1 && (
          <Card.Footer className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <div className="text-muted small mb-0">
              {pagination.total > 0
                ? `Showing ${startItem}-${endItem} of ${pagination.total} entries`
                : "No entries found"}
            </div>

            <Pagination className="mb-0">
              <Pagination.Prev
                disabled={!pagination.hasPrev}
                onClick={() => handlePageChange(currentPage - 1)}
              />

              {Array.from(
                { length: pagination.totalPages },
                (_, index) => index + 1
              ).map((pageNumber) => (
                <Pagination.Item
                  key={pageNumber}
                  active={pageNumber === currentPage}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </Pagination.Item>
              ))}

              <Pagination.Next
                disabled={!pagination.hasNext}
                onClick={() => handlePageChange(currentPage + 1)}
              />
            </Pagination>
          </Card.Footer>
        )}
      </Card>

      {/* View Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Feedback Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedQuery && (
            <>
              <p><strong>Name:</strong> {selectedQuery.name}</p>
              <p><strong>Email:</strong> {selectedQuery.email}</p>
              <p><strong>Mobile:</strong> {selectedQuery.mobile}</p>
              <p><strong>Type:</strong> {selectedQuery.feedback_type}</p>
              {/* <p><strong>Status:</strong> {getStatusBadge(selectedQuery.status)}</p> */}
              <hr />
              <p><strong>Message:</strong></p>
              <p>{selectedQuery.feedback}</p>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ContactReportsPage;
